import { api } from '../api/client'
import { batchListQuery, type BatchFilters } from './batchList'

export type TitleStatus = 'active' | 'lapsed' | 'cancelled' | 'gift' | 'other'

export interface TitleTag {
  slug: string
  abbr: string
  status: TitleStatus
  until: string | null
}

export interface SearchHit {
  subscriber_id: number
  name: string
  company: string | null
  place: string
  email: string | null
  account_number: string | null
  matched_on: string
  titles: TitleTag[]
  last_activity: string | null
  pays_by: string | null
}

export interface SearchResponse {
  q: string
  items: SearchHit[]
}

export interface SearchParams {
  q: string
  publication?: string
  status?: string
  limit?: number
}

export function deskSearch(p: SearchParams): Promise<SearchResponse> {
  const qs = new URLSearchParams({ q: p.q })
  if (p.publication) qs.set('publication', p.publication)
  if (p.status) qs.set('status', p.status)
  if (p.limit) qs.set('limit', String(p.limit))
  return api.get<SearchResponse>(`/api/desk/search?${qs.toString()}`)
}

// ---- member record (aamcirc/desk/history.py)
export type CardState = 'active' | 'lapsed' | 'cancelled' | 'never'

export interface TitleCard {
  slug: string
  name: string
  state: CardState
  /** The membership's own status (expired, on_hold, …); null on a never-had card. */
  status: string | null
  subscription_id: number | null
  end_date: string | null
  since: number | null
  paid_by: string | null
  auto_renew: boolean
  sc_owned: boolean
  last_payment: { order_key: string; method: string | null } | null
}

export interface GiftRow {
  subscriber_id: number
  name: string
  slug: string
  abbr: string
  state: CardState
  end_date: string | null
}

export interface MemberRecord {
  id: number
  name: string
  first_name?: string | null
  last_name?: string | null
  account_number: string | null
  email: string | null
  email_suppressed?: boolean
  do_not_email?: boolean
  email_block_reason?: string | null
  /** A sign-in link can still go: any block on the address is an opt-out. */
  sign_in_link_ok?: boolean
  postal_renewals?: boolean
  phone: string | null
  company: string | null
  address: { address_1: string | null; address_2: string | null; city: string | null; state: string | null; zipcode: string | null; country: string | null }
  tags: string[]
  member_since: number | null
  sc_owned_identity: boolean
  titles: TitleCard[]
  gifts_paid_for: GiftRow[]
  totals: { paid_cents: number; refunded_cents: number }
  notes: string | null
}

export type HistoryKind = 'all' | 'money' | 'comms' | 'change'

export interface HistoryItem {
  at: string
  kind: 'money' | 'comms' | 'call' | 'change'
  what: string
  title_abbr: string | null
  detail: string
  status: string
  tone: 'ok' | 'warn' | 'bad' | 'muted'
  by: string
  amount_cents: number | null
  order_key: string | null
  can_void: boolean
  can_refund: boolean
  /** A card, eCheck or PayPal refund (no refund check number asked for). */
  refund_via_gateway: boolean
  /** Paid minus refunded, on a payment; null otherwise. */
  refundable_cents: number | null
  is_payer: boolean
  /** A call forwarded to the Support desk: its contact conversation and title. */
  forwarded_message_id?: number | null
  forwarded_publication?: string | null
}

export interface HistoryPage {
  items: HistoryItem[]
  next_before: string | null
}

export const getMember = (id: number): Promise<MemberRecord> => api.get<MemberRecord>(`/api/desk/members/${id}`)

export function getHistory(id: number, kind: HistoryKind, before?: string | null, limit = 50): Promise<HistoryPage> {
  // URLSearchParams: next_before carries a "+" offset that must be encoded.
  const qs = new URLSearchParams({ kind, limit: String(limit) })
  if (before) qs.set('before', before)
  return api.get<HistoryPage>(`/api/desk/members/${id}/history?${qs.toString()}`)
}

export const patchDetails = (id: number, body: Record<string, unknown>): Promise<MemberRecord> =>
  api.patch<MemberRecord>(`/api/desk/members/${id}/details`, body)

export interface EndDateChange {
  subscription_id: number
  old_end: string | null
  new_end: string
  months_delta: number
}

export const patchEndDate = (
  id: number, subscriptionId: number,
  body: { new_end: string; reason: string; note: string; email_member: boolean },
): Promise<EndDateChange> =>
  api.patch<EndDateChange>(`/api/desk/members/${id}/subscriptions/${subscriptionId}/end-date`, body)

export const postCall = (
  id: number, body: { direction: 'in' | 'out'; publication_slug: string | null; note: string; forward?: boolean },
): Promise<HistoryItem> => api.post<HistoryItem>(`/api/desk/members/${id}/calls`, body)

export const postResend = (
  id: number, body: { what: string; publication_slug: string; email_log_id?: number },
): Promise<{ sent_to: string }> => api.post<{ sent_to: string }>(`/api/desk/members/${id}/resend`, body)

export interface PrintReceipt {
  what: 'receipt'; payer_name: string; address: string[]; order_key: string; date: string | null
  method: string | null; last4: string | null; total: string
  /** Name on the check, money order issuer, where cash was taken. */
  details: string[]
  lines: { title: string; term_months: number | null; for_whom: string | null; amount: string }[]
}
export interface PrintLabel { what: 'label'; name: string; company: string | null; address: string[] }
export interface PrintSummary {
  what: 'summary'; name: string; account_number: string | null; company: string | null; email: string | null
  phone: string | null; address: string[]
  titles: { name: string; state: string; end_date: string | null }[]
  payments: { order_key: string; date: string; method: string | null; amount: string }[]
}
export type PrintData = PrintReceipt | PrintLabel | PrintSummary

export const getPrint = (id: number, what: string, orderKey?: string | null): Promise<PrintData> =>
  api.get<PrintData>(`/api/desk/members/${id}/print/${encodeURIComponent(what)}${orderKey ? `?order_key=${encodeURIComponent(orderKey)}` : ''}`)

// ---- Record a payment (aamcirc/admin/routers/desk/payments.py)
export interface DeskPlan {
  publication_slug: string
  publication_name: string
  plan_id: string
  amount_cents: number
  term_months: number
}
export interface PaymentsConfig { phone_payments_enabled: boolean; echeck_enabled: boolean }
export interface MatchCandidate { subscriber_id: number; name: string; account_number: string | null; zipcode: string | null; score: number; tier: string }
export interface NewPersonBody {
  first_name?: string | null; last_name?: string | null; company?: string | null
  address_1?: string | null; address_2?: string | null
  city?: string | null; state?: string | null; zipcode?: string | null; country?: string | null
  email?: string | null; phone?: string | null
}
export interface PaymentLineBody {
  publication_slug: string; plan_id: string; price_cents: number; for: 'payer' | 'gift'
  recipient_subscriber_id?: number; new_recipient?: NewPersonBody
}
export interface PaymentResult {
  payer_subscriber_id: number
  orders: { order_key: string; publication_slug: string; beneficiary_subscriber_id: number; new_end: string | null }[]
  total_cents: number
  transaction_id?: string
  card_kept?: boolean
}

export const getPaymentPlans = (): Promise<{ plans: DeskPlan[] }> => api.get('/api/desk/payments/plans')
export const getPaymentsConfig = (): Promise<PaymentsConfig> => api.get('/api/desk/payments/config')
export const matchPeople = (p: NewPersonBody): Promise<{ candidates: MatchCandidate[] }> => api.post('/api/desk/payments/match', p)
export const postOfflinePayment = (body: Record<string, unknown>): Promise<PaymentResult> => api.post('/api/desk/payments/offline', body)
/** 202 from /phone: the processor is holding the charge for review; nothing was added. */
export interface HeldForReview { code: 'held_for_review'; transaction_id: string; message: string }
/** One entry of a 409 possible_duplicate_charge: a recent charge this one may repeat. */
export interface RecentCharge {
  when: string; amount_cents: number | null; titles: string[]; transaction_id: string | null
  status?: 'recorded' | 'unconfirmed' | 'held'
}
export const postPhonePayment = (body: Record<string, unknown>): Promise<PaymentResult | HeldForReview> => api.post('/api/desk/payments/phone', body)

export const postVoid = (body: { order_key: string; reason: string }): Promise<Record<string, unknown>> => api.post('/api/desk/payments/void', body)
export const postRefund = (body: { order_key: string; amount_cents: number; refund_reference?: string; reason: string; membership: 'end_now' | 'keep' | 'new_end'; new_end?: string }): Promise<Record<string, unknown>> =>
  api.post('/api/desk/payments/refund', body)

// ---- Mail Order Batches (aamcirc/admin/routers/desk/batches.py)
export interface BatchHead {
  id: number; label: string; opened_by: number | null
  opened_at: string; closed_at: string | null; deposited_at: string | null; archived_at: string | null
}
export interface BatchListRow extends BatchHead {
  status: 'open' | 'closed' | 'deposited'; opened_by_email: string | null; entries: number; amount_cents: number
}
export const listBatches = (f: BatchFilters): Promise<{ batches: BatchListRow[] }> => api.get(`/api/desk/batches${batchListQuery(f)}`)
export const openBatch = (): Promise<{ id: number; label: string }> => api.post('/api/desk/batches')
export const getBatch = (id: number): Promise<BatchHead & {
  entries: import('./batch').BatchEntry[]; totals: { count: number; amount_cents: number }
}> => api.get(`/api/desk/batches/${id}`)
export const getBatchSummary = (id: number): Promise<import('./batch').BatchSummary> => api.get(`/api/desk/batches/${id}/summary`)
export const batchAction = (id: number, what: 'close' | 'reopen' | 'deposited' | 'archive' | 'unarchive'): Promise<BatchHead> =>
  api.post(`/api/desk/batches/${id}/${what}`)
/** Only a batch nothing was ever keyed into; anything else is refused (409). */
export const deleteBatch = (id: number): Promise<{ id: number; deleted: true }> => api.del(`/api/desk/batches/${id}`)

// ---- activity (aamcirc/desk/activity.py)
export interface ActivityTotals {
  new: number
  renewed: number
  lapsed: number
  cancelled: number
  keyed: number
  revenue_cents: number
}
export interface ActivityBucket extends ActivityTotals {
  start: string
}
export interface ActivityTitleRow extends ActivityTotals {
  publication_slug: string
}
export interface ActivityResponse {
  range: { from: string; to: string; bucket: string; publication: string }
  compare: { from: string; to: string } | null
  totals: ActivityTotals
  compare_totals: ActivityTotals | null
  buckets: ActivityBucket[]
  by_title: ActivityTitleRow[]
  payments_by_method: { method: string; payments: number }[]
  revenue_source: string
  notes: string[]
}
export type ActivityMetric = 'new' | 'renewed' | 'lapsed' | 'cancelled' | 'net' | 'keyed' | 'revenue'
/** One drill-down row. Every field but the amount can be null on old or orphaned rows. */
export interface ActivityMemberRow {
  subscriber_id: number | null
  name: string
  account_number: string | null
  publication_slug: string | null
  publication_name: string | null
  at: string | null
  detail: string | null
  amount_cents: number | null
}
export interface ActivityParams {
  from: string
  to: string
  bucket?: string
  publication?: string
  compare_from?: string
  compare_to?: string
}

export function deskActivity(p: ActivityParams): Promise<ActivityResponse> {
  const qs = new URLSearchParams({ from: p.from, to: p.to, bucket: p.bucket ?? 'day', publication: p.publication || 'all' })
  if (p.compare_from && p.compare_to) { qs.set('compare_from', p.compare_from); qs.set('compare_to', p.compare_to) }
  return api.get<ActivityResponse>(`/api/desk/activity?${qs.toString()}`)
}

export function activityMembersUrl(metric: ActivityMetric, p: { from: string; to: string; publication?: string }, format: 'json' | 'csv', limit = 50, offset = 0): string {
  const qs = new URLSearchParams({ metric, from: p.from, to: p.to, publication: p.publication || 'all', format })
  if (format === 'json') { qs.set('limit', String(limit)); qs.set('offset', String(offset)) }
  return `/api/desk/activity/members?${qs.toString()}`
}

export function deskActivityMembers(metric: ActivityMetric, p: { from: string; to: string; publication?: string }, limit: number): Promise<{ total: number; items: ActivityMemberRow[] }> {
  return api.get(activityMembersUrl(metric, p, 'json', limit))
}

// ---- customer service card (aamcirc/desk/service.py)
export type ServiceChannel = 'email' | 'contact' | 'call' | 'feedback'
export type ServiceMood = 'pleased' | 'neutral' | 'unhappy'
export interface ServiceTotals {
  contacts: number
  members: number
  by_channel: Record<ServiceChannel, number>
  moods: Record<ServiceMood | 'unknown', number>
  unhappy_share: number | null
  reply_base: number
  within_day: number
  first_reply_median_s: number | null
}
export interface ServiceTopic { topic: string; label: string; count: number; unhappy: number; previous: number }
export interface ServiceTitleRow { publication_slug: string; contacts: number; active_members: number; per_1000: number | null }
export interface ServiceResponse {
  range: { from: string; to: string; publication: string }
  totals: ServiceTotals
  compare_totals: ServiceTotals | null
  active_members: number
  per_1000: number | null
  compare_per_1000: number | null
  all_titles_per_1000: number | null
  topics: ServiceTopic[]
  waiting: { count: number; oldest_at: string | null }
  by_title: ServiceTitleRow[]
}
export interface ServiceMemberRow {
  subscriber_id: number | null
  name: string
  account_number: string | null
  publication_slug: string | null
  publication_name: string | null
  at: string
  detail: string
  channel: ServiceChannel
  topic: string
  mood: ServiceMood | null
}
/** Who is behind a figure on the card: contacts (optionally one topic or mood) or those waiting now. */
export type ServiceDrill = { what: 'contacts'; topic?: string; mood?: ServiceMood } | { what: 'waiting' }

export function deskService(p: ActivityParams): Promise<ServiceResponse> {
  const qs = new URLSearchParams({ from: p.from, to: p.to, publication: p.publication || 'all' })
  if (p.compare_from && p.compare_to) { qs.set('compare_from', p.compare_from); qs.set('compare_to', p.compare_to) }
  return api.get<ServiceResponse>(`/api/desk/activity/service?${qs.toString()}`)
}

export function deskServiceMembers(d: ServiceDrill, p: { from: string; to: string; publication?: string }, limit: number): Promise<{ total: number; items: ServiceMemberRow[] }> {
  const qs = new URLSearchParams({ what: d.what, from: p.from, to: p.to, publication: p.publication || 'all', limit: String(limit) })
  if (d.what === 'contacts' && d.topic) qs.set('topic', d.topic)
  if (d.what === 'contacts' && d.mood) qs.set('mood', d.mood)
  return api.get(`/api/desk/activity/service/members?${qs.toString()}`)
}
